import { type FormEvent, useState } from 'react';
import { Field, Input } from '../components/form';
import { Alert, Button, Card } from '../components/ui';
import { ThemeSwitcher } from '../theme/ThemeSwitcher';
import styles from './screens.module.css';

/**
 * Cadastro público self-service do síndico (#80, ADR-013) — acessível sem login
 * em `/cadastro`. Envia a solicitação para a fila (POST /api/onboarding); o
 * super admin aprova depois. A rota é isenta de CSRF (pré-sessão), então basta
 * um POST de mesma origem. Nenhum tenant é criado aqui.
 */
export function OnboardingPublico() {
  const [razaoSocial, setRazaoSocial] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [nomeExibicao, setNomeExibicao] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cargo, setCargo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!razaoSocial.trim() || !cnpj.trim() || !nome.trim() || !email.trim()) {
      setErro('Preencha a razão social, o CNPJ e o seu nome e e-mail.');
      return;
    }
    setEnviando(true);
    let status = 0;
    try {
      const res = await fetch('/api/onboarding', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          razaoSocial: razaoSocial.trim(),
          cnpj: cnpj.trim(),
          nomeExibicao: nomeExibicao.trim() || undefined,
          responsavel: {
            nome: nome.trim(),
            email: email.trim(),
            telefone: telefone.trim() || undefined,
            cargo: cargo.trim() || undefined,
          },
        }),
      });
      status = res.status;
    } catch {
      status = 0;
    }
    setEnviando(false);
    if (status === 201) {
      setEnviado(true);
      return;
    }
    if (status === 409) setErro('Já existe um cadastro para este CNPJ. Fale com o suporte.');
    else if (status === 422) setErro('Verifique os dados: CNPJ inválido ou campos obrigatórios.');
    else if (status === 429) setErro('Muitas tentativas. Tente novamente em alguns minutos.');
    else setErro('Não foi possível enviar. Tente novamente.');
  }

  return (
    <div className={styles.centerScreen}>
      <Card className={styles.authCard}>
        <div className={styles.authHead}>
          <span className={styles.brand}>eCondomínio</span>
          <ThemeSwitcher compact />
        </div>

        {enviado ? (
          <>
            <h1>Solicitação enviada!</h1>
            <p className="muted">
              Recebemos o cadastro do seu condomínio. Nossa equipe vai analisar e, quando aprovado,
              você receberá as instruções de acesso por e-mail.
            </p>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button block variant="ghost" onClick={() => window.location.assign('/')}>
                ← Voltar ao início
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1>Cadastre seu condomínio</h1>
            <p className="muted">
              Preencha os dados abaixo. Após a aprovação, você recebe o acesso como síndico.
            </p>
            <form onSubmit={onSubmit} noValidate>
              <Field label="Razão social">
                {(id) => (
                  <Input
                    id={id}
                    value={razaoSocial}
                    onChange={(e) => setRazaoSocial(e.target.value)}
                  />
                )}
              </Field>
              <Field label="CNPJ">
                {(id) => (
                  <Input
                    id={id}
                    inputMode="numeric"
                    value={cnpj}
                    onChange={(e) => setCnpj(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Nome de exibição (opcional)">
                {(id) => (
                  <Input
                    id={id}
                    value={nomeExibicao}
                    onChange={(e) => setNomeExibicao(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Seu nome">
                {(id) => <Input id={id} value={nome} onChange={(e) => setNome(e.target.value)} />}
              </Field>
              <Field label="Seu e-mail">
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Telefone (opcional)">
                {(id) => (
                  <Input
                    id={id}
                    inputMode="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Seu cargo (opcional)">
                {(id) => <Input id={id} value={cargo} onChange={(e) => setCargo(e.target.value)} />}
              </Field>
              {erro ? <Alert kind="danger">{erro}</Alert> : null}
              <div style={{ marginTop: 'var(--space-4)' }}>
                <Button type="submit" block disabled={enviando}>
                  {enviando ? 'Enviando…' : 'Enviar solicitação'}
                </Button>
              </div>
              <div style={{ marginTop: 'var(--space-3)' }}>
                <Button block variant="ghost" onClick={() => window.location.assign('/')}>
                  Já tenho conta — entrar
                </Button>
              </div>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
